export type ApiErrorKind = "unauthorized" | "forbidden" | "not_found" | "conflict" | "validation" | "unavailable" | "unknown";

export const SESSION_ENDED_EVENT = "clearsight:session-ended";

type ErrorEnvelope = { message?: string; error?: string | { code?: string; message?: string } };

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly kind: ApiErrorKind;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.kind = kindFromStatus(status);
  }
}

export async function parseJSON<T>(response: Response): Promise<T> {
  if (!response.ok) throw await responseError(response);
  return await response.json() as T;
}

export async function requestJSON<T>(apiBase: string, path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!(init?.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  return parseJSON<T>(await fetchResponse(`${apiBase}${path}`, { ...init, credentials: init?.credentials ?? "include", headers }));
}

export async function requestVoid(apiBase: string, path: string, init?: RequestInit): Promise<void> {
  const headers = new Headers(init?.headers);
  if (!(init?.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetchResponse(`${apiBase}${path}`, { ...init, credentials: init?.credentials ?? "include", headers });
  if (!response.ok) throw await responseError(response);
}

export async function requestBlob(apiBase: string, path: string, init?: RequestInit): Promise<{ blob: Blob; filename?: string }> {
  const response = await fetchResponse(`${apiBase}${path}`, { ...init, credentials: init?.credentials ?? "include" });
  if (!response.ok) throw await responseError(response);
  return {
    blob: await response.blob(),
    filename: responseFilename(response.headers.get("Content-Disposition")),
  };
}

export function apiErrorKind(error: unknown): ApiErrorKind {
  return error instanceof ApiError ? error.kind : "unknown";
}

async function responseError(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => null) as ErrorEnvelope | null;
  const detail = typeof body?.error === "object" ? body.error : undefined;
  return new ApiError(
    response.status,
    detail?.message ?? body?.message ?? recoveryMessage(response.status),
    typeof body?.error === "string" ? body.error : detail?.code,
  );
}

async function fetchResponse(url: string, init?: RequestInit): Promise<Response> {
  try {
    const response = await fetch(url, init);
    if (response.status === 401 && typeof window !== "undefined" && !isDemoSessionEndpoint(url)) {
      window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
    }
    return response;
  } catch (cause) {
    if (init?.signal?.aborted || cause && typeof cause === "object" && "name" in cause && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "Connection lost. Check the record before trying again.", "connection_lost");
  }
}

function isDemoSessionEndpoint(url: string): boolean {
  if (typeof window === "undefined") return false;
  const path = new URL(url, window.location.origin).pathname;
  return path === "/api/v1/demo/login" || path === "/api/v1/demo/logout";
}

function recoveryMessage(status: number): string {
  if (status === 401) return "Session ended. Sign in again.";
  if (status === 403) return "Access denied. Check your permissions.";
  if (status === 404) return "Record not found. Reload the list.";
  if (status === 409 || status === 412) return "Record changed. Reload before saving.";
  if (status === 429) return "Too many requests. Try again shortly.";
  if (status >= 500) return "Service unavailable. Try again.";
  return "Request unavailable. Reload the record and try again.";
}

function responseFilename(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)?.[1];
  if (encoded) {
    try { return decodeURIComponent(encoded); } catch { return undefined; }
  }
  const quoted = /filename="([^"]+)"/i.exec(contentDisposition)?.[1];
  return quoted?.trim() || undefined;
}

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401) return "unauthorized";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409 || status === 412) return "conflict";
  if (status === 400 || status === 413 || status === 415 || status === 422) return "validation";
  if (status === 0 || status === 429 || status >= 500) return "unavailable";
  return "unknown";
}
